import { useEffect, useRef, useState } from "react";
import { fetchProducts } from "./api.js";

// Only the backend selects matching rows. Clear mismatched results immediately and
// guard both AbortController-aware and non-cancellable transports against stale replies.
export function useRemoteProducts(query, enabled, revision = 0) {
  const key = JSON.stringify(query);
  const currentKey = useRef(key);
  currentKey.current = key;
  const sequence = useRef(0);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [maxPrice, setMaxPrice] = useState(0);
  const [minPrice, setMinPrice] = useState(0);
  const [noPriceCount, setNoPriceCount] = useState(0);
  const [resultKey, setResultKey] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const requestId = ++sequence.current;
    const controller = new AbortController();
    setProducts([]);
    setTotal(0);
    setMinPrice(0);
    setMaxPrice(0);
    setNoPriceCount(0);
    setResultKey(null);
    setError("");
    if (!enabled) {
      setLoading(false);
      return () => controller.abort();
    }
    setLoading(true);
    const isCurrent = () =>
      !controller.signal.aborted &&
      requestId === sequence.current &&
      currentKey.current === key;
    fetchProducts(JSON.parse(key), { signal: controller.signal })
      .then((result) => {
        if (!isCurrent()) return;
        if (
          !Array.isArray(result.items) ||
          !Number.isInteger(result.total) ||
          result.total !== result.items.length
        )
          throw new Error(
            "商品接口必须返回当前条件的完整 items 及一致的 total，不能仅返回单页数据。",
          );
        setProducts(result.items);
        setTotal(result.total);
        setMaxPrice(result.maxPrice);
        setMinPrice(result.minPrice);
        setNoPriceCount(result.noPriceCount);
        setResultKey(key);
      })
      .catch((reason) => {
        if (isCurrent()) {
          setError(reason.message || "商品加载失败");
          setResultKey(key);
        }
      })
      .finally(() => {
        if (isCurrent()) setLoading(false);
      });
    return () => controller.abort();
  }, [key, enabled, revision]);

  return {
    products: enabled && resultKey === key ? products : [],
    total: enabled && resultKey === key ? total : 0,
    error: resultKey === key ? error : "",
    loading: enabled && (loading || resultKey !== key),
    maxPrice: enabled && resultKey === key ? maxPrice : 0,
    minPrice: enabled && resultKey === key ? minPrice : 0,
    noPriceCount: enabled && resultKey === key ? noPriceCount : 0,
  };
}
